import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import Header from './components/Header';
import CookieConsent from './components/CookieConsent';
import CallbackPopup from './components/CallbackPopup';
import Hero from './components/Hero';
import HowItWorks from './components/HowItWorks';
import Features from './components/Features';
import Integrations from './components/Integrations';
import Benefits from './components/Benefits';
import Footer from './components/Footer';
import Terms from './components/Terms';
import PrivacyPolicy from './components/Privacy';
import MobileCategoryUI from './components/MobileCategoryUI';

import LastSection from './components/LastSection';
import Pricing from './components/Pricing';
import HowItWorksFlowchart from './components/HowItWorksFlowchart';
import AboutWhiteOwl from './components/AboutWhiteOwl';
import CarouselCards from './components/CarouselCards';
import MobileCarouselCards from './components/MobileCarouselCards';
import StickyLayoutWrapper from './components/StickyLayoutWrapper';
import InitialSectionContent from './components/InitialSectionContent';
import WhiteOwlVideoSection from './components/WhiteOwlVideoSection';
import StatsCards from './components/StatsCards';
import ComparisonTable from './components/ComparisonTable';
import SettingBilling from './components/SettingBilling';
import MobileHero from './components/MobileHero';
import CaseStudyCarouselContent from './components/CaseStudyCarouselContent';
import EarlyAccessForm from './components/EarlyAccessForm';
import NewHome from './components/NewHome';
import Onboarding from './components/Onboarding';
import PaymentSuccess from './components/PaymentSuccess';
import { trackPageView, captureUtmSource } from './utils/mixpanelClient';

// ScrollToTop component to scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

// Capture UTM once, then track SPA page views on every route change.
const MixpanelPageTracker = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    captureUtmSource();
  }, []);

  useEffect(() => {
    trackPageView(pathname);
  }, [pathname]);

  return null;
};

// MetaTags component to update meta tags based on route
const MetaTags = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    const metaData = {
      '/': {
        title: 'WhiteOwl | AI Growth Platform for Consulting & Professional Services Firms',
        description: 'WhiteOwl — the AI Growth Platform for consulting and professional services firms. Buddy watches signals, preps your day, drafts outreach. AI prepares. You decide.'
      },
      '/NewHome': {
        title: 'AI Sales Platform for Consulting Firms- Discover. Qualify. Win. | WhiteOwl',
        description: 'From discovering new leads to deal closure, automate your entire sales process with agentic AI.'
      },
      '/pricing': {
        title: 'WhiteOwl Pricing | Start with 1000 Free Credits',
        description: 'Get started with a free account of WhiteOwl AI. Keep track of your target account, get more leads and improve winnability.'
      },
      '/privacy': {
        title: 'WhiteOwl AI | Privacy Policy',
        description: 'Learn how we collect, use, and protect your personal and business data when you use our platform.'
      },
      '/terms': {
        title: 'WhiteOwl AI | Terms of Service',
        description: 'Review the terms and conditions governing your use of the WhiteOwl platform and services.'
      },
      '/about': {
        title: 'WhiteOwl AI | About We Are',
        description: 'Get to know the team behind the platform and our commitment to security, reliability, and results.'
      },
      '/onboarding': {
        title: 'Get Started | WhiteOwl',
        description: 'Enter your company website and start using WhiteOwl — see account signals, contact intel, and WO Buddy with 1,000 free credits.'
      },
      '/payment/success': {
        title: 'Payment Successful | WhiteOwl',
        description: 'Your WhiteOwl payment was successful. We are preparing your account.'
      }
    };

    const currentMeta = metaData[pathname] || metaData['/'];

    // Update title
    document.title = currentMeta.title;

    // Update or create meta description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', currentMeta.description);
    } else {
      metaDescription = document.createElement('meta');
      metaDescription.name = 'description';
      metaDescription.content = currentMeta.description;
      document.head.appendChild(metaDescription);
    }

    // Update or create Open Graph meta tags for better social sharing
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute('content', currentMeta.title);
    } else {
      const meta = document.createElement('meta');
      meta.setAttribute('property', 'og:title');
      meta.content = currentMeta.title;
      document.head.appendChild(meta);
    }

    const ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) {
      ogDescription.setAttribute('content', currentMeta.description);
    } else {
      const meta = document.createElement('meta');
      meta.setAttribute('property', 'og:description');
      meta.content = currentMeta.description;
      document.head.appendChild(meta);
    }
  }, [pathname]);

  return null;
};

const HomePage = () => (
  <>
    <Hero />
    {/* <WhatIsZoi /> */}
    <HowItWorks />
    <Features />
    <Integrations />
    {/* <ComparisonTable /> */}
    <Benefits />
    <LastSection />
    {/* <AboutUs /> */}
    {/* <EarlyAccess /> */}
    {/* <EarlyAccessForm /> */}
    {/* <FAQ /> */}
  </>
);

const HomePageNew = () => (
  <>
    <StickyLayoutWrapper>
      <InitialSectionContent />
      <WhiteOwlVideoSection />
      {/* <StatsCards /> */}
      <CaseStudyCarouselContent />
    </StickyLayoutWrapper>
    <HowItWorksFlowchart />
    <div className="bg-gray-100">
      <CarouselCards />
      <ComparisonTable />
      <AboutWhiteOwl />
    </div>
  </>
);

const MobileHomePage = () => (
  <>
    <MobileHero />
    <WhiteOwlVideoSection />
    <StatsCards />
    <HowItWorksFlowchart />
    <MobileCarouselCards />
    <AboutWhiteOwl />
  </>
);

const MobileCategoryPage = ({ onOpenSignUpModal }) => (
  <>
    <Header onOpenSignUpModal={onOpenSignUpModal} />
    <MobileCategoryUI/>
  </>
);

// Responsive wrapper component that shows desktop or mobile page based on screen size
const ResponsiveHomePage = () => {
  return (
    <>
      {/* Desktop view - hidden on mobile (below md breakpoint) */}
      <div className="hidden md:block">
        <HomePageNew />
      </div>
      {/* Mobile view - visible only on mobile (below md breakpoint) */}
      <div className="block md:hidden">
        <MobileHomePage />
      </div>
    </>
  );
};

// Renders the routed page, hiding the global Header/Footer chrome on
// / since that page ships its own nav + footer.
const AppBody = ({ showSignUpModal, setShowSignUpModal, signUpContext, openSignUpModal }) => {
  const location = useLocation();
  const isNewHome = location.pathname === '/' || location.pathname === '/pricing';
  // Terms, Privacy, Onboarding, and Payment Success ship their own chrome, so skip the shared Header/Footer.
  const hasOwnChrome =
    isNewHome
    || location.pathname === '/terms'
    || location.pathname === '/privacy'
    || location.pathname === '/onboarding'
    || location.pathname === '/payment/success';

  return (
    <div className="min-h-screen bg-white">
      {!hasOwnChrome && <Header onOpenSignUpModal={() => openSignUpModal()} />}
      <main className="pt-0">
        <Routes>
          <Route path="/" element={<NewHome />} />
          <Route path="/pricing" element={<NewHome />} />
          <Route path="/payment/success" element={<PaymentSuccess />} />
          <Route path="/NewHome" element={<ResponsiveHomePage />} />
          <Route path="/onboarding" element={<Onboarding />} />
          <Route path="/category" element={<MobileCategoryPage onOpenSignUpModal={() => openSignUpModal()} />} />
          <Route path="/Pricing" element={<SettingBilling onOpenSignUpModal={openSignUpModal} />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
        </Routes>
      </main>
      {!hasOwnChrome && <Footer />}
      <CookieConsent />
      <CallbackPopup />

      {showSignUpModal && (
        <div
          className="fixed inset-0 z-[9999] bg-black bg-opacity-50 flex items-center justify-center p-4"
          onClick={() => setShowSignUpModal(false)}
        >
          <div onClick={(e) => e.stopPropagation()}>
            <EarlyAccessForm mode={signUpContext.mode} planId={signUpContext.planId} />
          </div>
        </div>
      )}
    </div>
  );
};

function App() {
  const [showSignUpModal, setShowSignUpModal] = useState(false);
  const [signUpContext, setSignUpContext] = useState({ mode: 'default', planId: null });

  const openSignUpModal = (context = { mode: 'default', planId: null }) => {
    setSignUpContext({
      mode: context?.mode || 'default',
      planId: context?.planId ?? null,
    });
    setShowSignUpModal(true);
  };

  return (
    <Router>
      <ScrollToTop />
      <MixpanelPageTracker />
      <MetaTags />
      <AppBody
        showSignUpModal={showSignUpModal}
        setShowSignUpModal={setShowSignUpModal}
        signUpContext={signUpContext}
        openSignUpModal={openSignUpModal}
      />
    </Router>
  );
}

export default App;
